import { Component, OnInit, signal, computed, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { 
  Firestore, collection, getDocs, deleteDoc, doc, onSnapshot, docData, 
  query, where, updateDoc, QuerySnapshot, DocumentData, QueryDocumentSnapshot, getDoc 
} from '@angular/fire/firestore';
import { Storage, ref, deleteObject } from '@angular/fire/storage';
import { VideoDetailsComponent } from '@shared/components/video-details/video-details.component';
import { Video, VideoStatus } from '@shared/models/video.model';
import { UserService } from '@shared/services/user.service';
import { User } from '@shared/models/user.model';
import { FormatDatePipe } from '@shared/pipes/format-date.pipe';
import { TimestampToDatePipe } from '@shared/pipes/timestamp-to-date.pipe';
import { DeleteRunwayService } from '@shared/services/delete-runway.service';
import { ToastService } from '@shared/services/toast.service';
import { RecordingService } from '@shared/services/recording.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Timestamp, UpdateData } from 'firebase/firestore'; // Correct import

@Component({
  selector: 'app-all-videos',
  standalone: true,
  imports: [CommonModule, VideoDetailsComponent, DatePipe, FormatDatePipe, TimestampToDatePipe],
  templateUrl: './all-videos.component.html',
  styleUrls: ['./all-videos.component.css']
})
export class AllVideosComponent implements OnInit {
  private firestore: Firestore = inject(Firestore);
  private storage: Storage = inject(Storage);
  private userService = inject(UserService);
  private formatDatePipe = inject(FormatDatePipe);
  private deleteRunwayService = inject(DeleteRunwayService);
  private toastService = inject(ToastService);
  private recordingService = inject(RecordingService);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);

  private videosSignal = signal<Video[]>([]);
  videos = computed(() => this.videosSignal());
  isViewVideoModalOpen = signal(false);
  editingVideo = signal<Video | null>(null);
  
  user = signal<User | null>(null);
  isSuperAdmin = computed(() => this.user()?.roles?.includes('superadmin') ?? false);
  currentTab = signal<VideoStatus>('submitted');
  
  filteredVideos = computed(() => {
    const videos = this.videos().filter(video => video.status === this.currentTab());
    return videos.sort((a, b) => {
      const aDate = this.convertTimestamp(a.createdAt);
      const bDate = this.convertTimestamp(b.createdAt);
      return this.sortDirection() === 'asc' 
        ? aDate.getTime() - bDate.getTime()
        : bDate.getTime() - aDate.getTime();
    });
  });

  sortDirection = signal<'asc' | 'desc'>('desc');

  ngOnInit() {
    this.subscribeToVideos();
    this.userService.getUserState().subscribe(user => {
      this.user.set(user);
    });

    // Handle incoming videoId parameter
    this.route.queryParams.subscribe(params => {
      const videoId = params['videoId'];
      if (videoId) {
        // Wait for videos to be loaded before trying to open
        const checkAndOpenVideo = () => {
          const video = this.videos().find(v => v.id === videoId);
          if (video) {
            this.viewVideo(video);
          } else {
            // If video not found yet, wait a bit and try again
            setTimeout(checkAndOpenVideo, 100);
          }
        };
        checkAndOpenVideo();
      }
    });
  }

  private subscribeToVideos() {
    const videosCollection = collection(this.firestore, 'videos');
    onSnapshot(videosCollection, (snapshot) => {
      const videosData = snapshot.docs.map(doc => this.convertToVideo(doc));
      this.videosSignal.set(videosData);
    });
  }

  private convertToVideo(doc: any): Video {
    const data = doc.data();
    const createdAt = this.ensureValidDate(data['createdAt']);
    const patientDateOfBirth = this.parseDate(data['patientDateOfBirth']);
    
    return {
      id: doc.id,
      userEmail: data['userEmail'] as string,
      patientFirstName: data['patientFirstName'] as string,
      patientLastName: data['patientLastName'] as string,
      patientDateOfBirth: patientDateOfBirth ? new Date(this.formatDatePipe.transform(patientDateOfBirth) || '') : null,
      description: data['description'] as string,
      status: data['status'] as VideoStatus,
      createdAt: createdAt,
      userId: data['userId'] as string,
      videoUrl: data['videoUrl'] as string | undefined,
      priorityScore: data['priorityScore'] as number,
      additionalInfo: data['additionalInfo'] || { symptoms: {}, conditions: {} },
      uploader: data['uploader'] as string,
      uploaderEmail: data['uploaderEmail'] as string,
      uploaderOccupation: data['uploaderOccupation'] as string,
      uploaderOrganization: data['uploaderOrganization'] as string
    };
  }

  private ensureValidDate(value: any): Date | Timestamp {
    if (value instanceof Timestamp) {
      return value;
    }
    if (value instanceof Date) {
      return value;
    }
    if (typeof value === 'string') {
      const parsedDate = new Date(value);
      return isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
    }
    if (typeof value === 'object' && value && 'seconds' in value && 'nanoseconds' in value) {
      return new Timestamp(value.seconds, value.nanoseconds);
    }
    return new Date(); // Default to current date if all else fails
  }

  private parseDate(value: any): Date | null {
    if (value instanceof Timestamp) {
      return value.toDate();
    }
    if (value instanceof Date) {
      return value;
    }
    if (typeof value === 'string') {
      const parsedDate = new Date(value);
      return isNaN(parsedDate.getTime()) ? null : parsedDate;
    }
    if (typeof value === 'object' && value && 'seconds' in value && 'nanoseconds' in value) {
      return new Date(value.seconds * 1000 + value.nanoseconds / 1000000);
    }
    return null;
  }

  viewVideo(video: Video) {
    console.log('Viewing video:', video);
    console.log('Video URL:', video.videoUrl);
    this.editingVideo.set(video);
    this.isViewVideoModalOpen.set(true);
    // Update URL with videoId without navigating
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { videoId: video.id },
      queryParamsHandling: 'merge',
      replaceUrl: false
    });
  }

  onDrawerChange(event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    if (isChecked) {
      this.openDrawer();
    } else {
      this.closeDrawer();
    }
  }

  openDrawer(): void {
    this.isViewVideoModalOpen.set(true);
  }

  closeDrawer(): void {
    this.isViewVideoModalOpen.set(false);
    this.editingVideo.set(null);
  }

  onVideoSaved() {
    // Implement video save logic if needed
    this.closeDrawer();
  }

  onVideoDraftSaved() {
    // Implement draft save logic if needed
    this.closeDrawer();
  }

  updateVideoStatus(video: Video, newStatus: VideoStatus) {
    const videoRef = doc(this.firestore, `videos/${video.id}`);
    updateDoc(videoRef, { status: newStatus })
      .then(() => {
        console.log('Video status updated successfully');
        // Update the local video object
        video.status = newStatus;
      })
      .catch((error: Error) => {
        console.error('Error updating video status:', error);
      });
  }

  isTabActive(tab: VideoStatus): boolean {
    return this.currentTab() === tab;
  }

  setTab(tab: VideoStatus): void {
    this.currentTab.set(tab);
  }

  getVideoCount(status: VideoStatus): number {
    return this.videos().filter(video => video.status === status).length;
  }

  async deleteVideo(video: Video) {
    if (confirm('Are you sure you want to delete this video?')) {
      try {
        if (video.videoUrl) {
          await this.deleteRunwayService.moveToDeleteRunway(video.videoUrl);
        }

        // Delete the Firestore document
        await this.deleteRunwayService.deleteFirestoreDocument('videos', video.id!);

        console.log('Video deleted successfully');
        this.toastService.show('Video moved to delete runway', 'success');
        
        // Update the local state
        this.videosSignal.update(videos => videos.filter(v => v.id !== video.id));
      } catch (error) {
        console.error('Error deleting video:', error);
        this.toastService.show('Error deleting video', 'error');
      }
    }
  }

  openVideoById(videoId: string) {
    const video = this.videos().find(v => v.id === videoId);
    if (video) {
      this.viewVideo(video);
    }
  }

  toggleSort() {
    this.sortDirection.update(current => current === 'asc' ? 'desc' : 'asc');
  }

  async fixUploaderDetails() {
    try {
      const videosCollection = collection(this.firestore, 'videos');
      const videosSnapshot = await getDocs(videosCollection);
      let fixedCount = 0;
      
      for (const videoDoc of videosSnapshot.docs) {
        const videoData = videoDoc.data() as Video;
        
        // Skip if already has complete uploader details
        if (videoData.uploader && videoData.uploaderEmail && 
            videoData.uploaderOccupation && videoData.uploaderOrganization) {
          continue;
        }

        // Get user data
        try {
          const userDoc = await getDoc(doc(this.firestore, 'users', videoData.userId));
          if (userDoc.exists()) {
            const userData = userDoc.data() as User;
            
            // Update video with user information
            const updatedVideo = {
              uploader: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
              uploaderEmail: userData.email || userData.workEmail,
              uploaderOccupation: userData.occupation || '',
              uploaderOrganization: userData.organizationName || ''
            };

            await updateDoc(doc(this.firestore, 'videos', videoDoc.id), updatedVideo);
            fixedCount++;
          }
        } catch (error) {
          console.error(`Error updating video ${videoDoc.id}:`, error);
        }
      }

      this.toastService.show(
        `Fixed uploader details for ${fixedCount} video${fixedCount !== 1 ? 's' : ''}`, 
        'success'
      );
    } catch (error) {
      console.error('Error fixing uploader details:', error);
      this.toastService.show('Error fixing uploader details', 'error');
    }
  }

  async fixDisconnectedRecordings() {
    if (confirm('This will attempt to fix disconnected recordings and videos. Continue?')) {
      try {
        this.toastService.show('Starting to fix disconnected recordings...', 'info');
        
        const result = await this.recordingService.fixDisconnectedRecordings();
        
        if (result.fixed > 0) {
          this.toastService.show(`Fixed ${result.fixed} disconnected recordings/videos`, 'success');
        } else if (result.errors > 0) {
          this.toastService.show(`No recordings fixed. Encountered ${result.errors} errors.`, 'error');
        } else {
          this.toastService.show('No disconnected recordings found to fix', 'info');
        }
      } catch (error) {
        console.error('Error fixing disconnected recordings:', error);
        this.toastService.show('Error fixing disconnected recordings', 'error');
      }
    }
  }

  async fixDuplicates(video: Video | null) {
    if (!this.isSuperAdmin()) {
      this.toastService.show('Only superadmin users can merge duplicates', 'error');
      return;
    }

    try {
      // If no specific video is provided, run global fix
      if (!video) {
        this.toastService.show('Starting comprehensive fix for all video and recording issues...', 'info');

        // Run all fixes
        const blobResult = await this.recordingService.fixBlobUrlVideos();
        console.log('Fixed blob URLs:', blobResult);

        const disconnectedResult = await this.recordingService.fixDisconnectedRecordings();
        console.log('Fixed disconnected recordings:', disconnectedResult);

        const linksResult = await this.recordingService.ensureVideoRecordingLinks();
        console.log('Fixed video-recording links:', linksResult);

        const videosResult = await this.recordingService.ensureRecordingsHaveVideos();
        console.log('Ensured recordings have videos:', videosResult);

        const totalFixed = blobResult.fixed + disconnectedResult.fixed + linksResult.fixed + videosResult.fixed;
        const totalErrors = blobResult.errors + disconnectedResult.errors + linksResult.errors + videosResult.errors;

        if (totalFixed > 0) {
          this.toastService.show(`Successfully fixed ${totalFixed} issues across all videos`, 'success');
          this.subscribeToVideos();
        } else if (totalErrors > 0) {
          this.toastService.show(`Failed to fix some issues. Check console for details.`, 'error');
        } else {
          this.toastService.show('No issues found to fix', 'info');
        }
        return;
      }

      // Handle specific video fix
      this.toastService.show('Checking for duplicates...', 'info');

      // First check if this video has a recordingId
      if (!video.recordingId) {
        this.toastService.show('No recording ID found for this video', 'error');
        return;
      }

      // Get the recording document
      const recordingDoc = await getDoc(doc(this.firestore, 'recordings', video.recordingId));
      if (!recordingDoc.exists()) {
        this.toastService.show('Recording not found', 'error');
        return;
      }

      const recording = recordingDoc.data();
      
      // Look for other videos with the same recording ID or matching criteria
      const videosRef = collection(this.firestore, 'videos');
      const q = query(
        videosRef,
        where('userId', '==', video.userId),
        where('createdAt', '>=', new Date(this.convertTimestamp(video.createdAt).getTime() - 300000)), // within 5 minutes before
        where('createdAt', '<=', new Date(this.convertTimestamp(video.createdAt).getTime() + 300000))  // within 5 minutes after
      );

      const querySnapshot = await getDocs(q);
      const potentialDuplicates = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(v => v.id !== video.id); // Exclude the current video

      if (potentialDuplicates.length === 0) {
        this.toastService.show('No duplicates found for this video', 'info');
        return;
      }

      // Fix the duplicates
      const blobResult = await this.recordingService.fixBlobUrlVideos();
      console.log('Fixed blob URLs:', blobResult);

      const disconnectedResult = await this.recordingService.fixDisconnectedRecordings();
      console.log('Fixed disconnected recordings:', disconnectedResult);

      const linksResult = await this.recordingService.ensureVideoRecordingLinks();
      console.log('Fixed video-recording links:', linksResult);

      const videosResult = await this.recordingService.ensureRecordingsHaveVideos();
      console.log('Ensured recordings have videos:', videosResult);

      const totalFixed = blobResult.fixed + disconnectedResult.fixed + linksResult.fixed + videosResult.fixed;
      const totalErrors = blobResult.errors + disconnectedResult.errors + linksResult.errors + videosResult.errors;

      if (totalFixed > 0) {
        this.toastService.show(`Successfully fixed ${totalFixed} issues with this video and its duplicates`, 'success');
        this.subscribeToVideos();
      } else if (totalErrors > 0) {
        this.toastService.show(`Failed to fix some issues. Check console for details.`, 'error');
      } else {
        this.toastService.show('No issues found to fix', 'info');
      }

    } catch (error) {
      console.error('Error fixing duplicates:', error);
      this.toastService.show('Error fixing duplicates', 'error');
    }
  }

  private convertTimestamp(timestamp: Timestamp | Date): Date {
    if (timestamp instanceof Timestamp) {
      return timestamp.toDate();
    }
    return timestamp as Date;
  }
}